import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize, map, Observable, of, tap } from 'rxjs';

import { IdentityPerson, IdentityTeam, PagedResponse, Role, Wrestler } from '@reg/shared/model';
import { PersonSuggestion } from '@reg/identity/search/person-suggestion.model';
import { ConfigurationService, HttpServiceHelper, IdentityTeamService } from '@reg/shared/service';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';
import { observableInSeparateThread } from '@reg/shared/helper/observable-helper';

/**
 * Service to get and save person data.
 */
@Injectable()
export class IdentityPersonService {
    public static PARAM_USER_PERMISSIONS_META: string = 'userPermissionsMeta';
    public static INCLUDE_TEAMS_AS_ATHLETE: string = 'teamsAsAthlete';
    public static INCLUDE_ATHLETES_AS_PARENT: string = 'athletesAsParent.organizationPeople';
    public static INCLUDE_USER_W_ASSESSORS: string = 'user.assessors';
    public static INCLUDE_BODY_ASSESSMENTS: string = 'bodyAssessments';
    public static INCLUDE_PARENTS_AS_ATHLETE: string = 'parentsAsAthlete.organizationPeople';
    public static INCLUDE_BODY_ASSESSMENTS_W_SEASON: string = 'bodyAssessments.season';
    public static INCLUDE_USER: string = 'user';
    public static INCLUDE_ORGANIZATION_PEOPLE: string = 'organizationPeople';

    private readonly identityApiBaseUrl: string;
    private readonly identitySportName: string;
    private cachedPersonMap: { [apiRequest: string]: IdentityPerson; } = {};
    private cachedPeopleMap: { [apiRequest: string]: PagedResponse; } = {};

    /**
     * Constructor
     */
    constructor(
                private http: HttpServiceHelper,
                private configService: ConfigurationService,
                private identityTeamService: IdentityTeamService,
    ) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
        this.identitySportName = http.configService.getSportConfig('identitySportName');
    }

    public clearCache(): void {
        this.cachedPersonMap = {};
        this.cachedPeopleMap = {};
    }

    public getAthleteProfileUrl(identityPersonId: string): string {
        const sportConfigHeader: any = this.configService.getSportConfig('header');

        if (identityPersonId &&
            sportConfigHeader &&
            sportConfigHeader.siteUrl) {
            return `${sportConfigHeader.siteUrl}/athletes/${identityPersonId}`;
        }

        return null;
    }

    /**
     * Get all people for the specified query params
     */
    public getIndexPagedResponse(
        includes: string[] = null,
        queryParams: string[] = null,
        forceRefresh: boolean = false,
    ): Observable<PagedResponse> {
        queryParams = this.appendGetFields(queryParams);
        includes = this.appendOrganizationPeople(includes);
        const apiRequestUrl: string = this.getApiUrl(null, includes, queryParams);
        return this.getIndexPagedResponseByUrl(apiRequestUrl, forceRefresh);
    }

    /**
     * Get people by a url. This is used for paging through people/athlets since the response
     * provides the proper page urls
     */
    public getIndexPagedResponseByUrl(url: string, forceRefresh: boolean = false): Observable<PagedResponse> {
        if (!forceRefresh &&
            this.cachedPeopleMap[url]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedPeopleMap[url].clone());
        }
        return this.http.get(url)
            .pipe(map((response: IJsonApiResponse) => {
                const json: any = response;

                const people: IdentityPerson[] = [];
                const included: Object[] = response.included;

                const bodyData: any = response.data || [];
                for (const person of bodyData) {
                    people.push((new IdentityPerson()).deserialize(person, included, this.identitySportName));
                }

                const pagedResponse: PagedResponse = new PagedResponse(
                    json['links']['first'],
                    json['links']['last'],
                    json['links']['prev'],
                    json['links']['next'],
                    json['meta']['total'],
                    people,
                );

                this.cachedPeopleMap[url] = pagedResponse.clone();

                return pagedResponse;
            }));
    }

    public getCurrentUser(includes: string[] = null): Observable<IdentityPerson> {
        let queryParams: string[] = ['userId=current'];
        queryParams = this.appendGetFields(queryParams);
        includes = this.appendOrganizationPeople(includes);

        return this.getIndexPagedResponse(includes, queryParams).pipe(map(
            (peopleResponse: PagedResponse) => {
                if (peopleResponse && peopleResponse.recordList.length > 0) {
                    return peopleResponse.recordList[0];
                }
                return null;
            },
        ));
    }

    /**
     * search for persons
     */
    public search(wrestler: Wrestler): Observable<PersonSuggestion[]> {
        if (!wrestler) {
            return of(null);
        }

        const orParams: string[] = [];
        const termOrParams: string[] = [];
        const andParams: string[] = [];

        andParams.push('(term field=entity_name \'person\')');

        if (wrestler.usaWrestlingCardNum) {
            // If we have the usa wrestling number we should be able to find a match if we have the record
            termOrParams.push(`(term field=usa_wrestler_id boost=10 '${wrestler.usaWrestlingCardNum}')`);
        }
        if (wrestler.lastName) {
            const lastName: string = encodeURIComponent(wrestler.lastName.trim().replace(/'/g, '\\\''));
            const prefix: string = `(prefix field=last_name boost=5 '${lastName}')`;
            // Only return results that start with the given last name
            andParams.push(prefix);
            orParams.push(prefix);
        }
        if (wrestler.firstName) {
            const firstName: string = encodeURIComponent(wrestler.firstName.trim().replace(/'/g, '\\\''));
            orParams.push(`(prefix field=first_name boost=5 '${firstName}')`);
            termOrParams.push(`(term field=first_name boost=5 '${firstName}')`);
        }
        if (wrestler.team && wrestler.team.identityTeamId) {
            termOrParams.push(`(term field=team_ids boost=5 '${wrestler.team.identityTeamId}')`);
        }

        let query: string = `(and ${andParams.join(' ')}`;

        if (orParams.length > 0 || termOrParams.length > 0) {
            // Make sure the term or params are last
            query += `(or ${orParams.join(' ')} ${termOrParams.join(' ')})`;
        }

        query += ')';
        query += '&q.parser=structured';
        let latitude: number;
        let longitude: number;

        if (wrestler.location &&
            wrestler.location.latitude &&
            wrestler.location.longitude) {
            latitude = wrestler.location.latitude;
            longitude = wrestler.location.longitude;
        } else if (wrestler.team &&
            wrestler.team.location &&
            wrestler.team.location.latitude &&
            wrestler.team.location.longitude) {
            latitude = wrestler.team.location.latitude;
            longitude = wrestler.team.location.longitude;
        }

        if (latitude && longitude) {
            // Take the person's distance into consideration when searching so closer results return first
            query += `&expr_distance=haversin(${latitude},${longitude
            },location_latlon.latitude,location_latlon.longitude)`;
        }

        let apiRequestUrl: string = `${this.getApiUrl(null, null, null)}search`;
        apiRequestUrl += this.http.getQueryParameters(null, [`q=${query}`]);

        return this.http.get(apiRequestUrl, false)
            .pipe(map((response: IJsonApiResponse) => {
                const personSuggestions: PersonSuggestion[] = [];

                const responseBody: any = response;
                if (responseBody?.hits?.hit) {
                    for (const hit of responseBody.hits.hit) {
                        personSuggestions.push((new PersonSuggestion()).fillFromSearchHit(hit));
                    }
                }

                return personSuggestions;
            }));
    }

    /**
     * Get a single person's data
     */
    public get(
        identityPersonId: string,
        displayErrors: boolean = true,
        includes: string[] = null,
        forceRefresh: boolean = false,
    ): Observable<IdentityPerson> {
        const queryParams: string[] = this.appendGetFields([]);
        includes = this.appendOrganizationPeople(includes);

        if (queryParams.filter(
            (x: string) => x.indexOf(IdentityPersonService.PARAM_USER_PERMISSIONS_META) !== -1,
        ).length === 0) {
            queryParams.push(`${IdentityPersonService.PARAM_USER_PERMISSIONS_META}=1`);
        }

        if (!identityPersonId) {
            return of(null);
        }

        const apiRequestUrl: string = this.getApiUrl(identityPersonId, includes, queryParams);

        if (!forceRefresh && this.cachedPersonMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedPersonMap[apiRequestUrl].clone());
        }
        // Make the http request
        return this.http.get(apiRequestUrl, true, displayErrors)
            .pipe(map((response: IJsonApiResponse) => {
                const identityPerson: IdentityPerson =
                        (new IdentityPerson().deserialize(response, null, this.identitySportName));
                this.cachedPersonMap[apiRequestUrl] = identityPerson.clone();
                return identityPerson;
            }));
    }

    /**
     * Add or update the person
     */
    public save(
        person: IdentityPerson,
        roleId: string,
        includes: string[] = null,
        forceSaveAllValues: boolean = false,
    ): Observable<IdentityPerson> {
        // Clear cached results
        this.clearCache();
        includes = includes || [];
        const queryParams: string[] = this.appendGetFields();
        includes = this.appendOrganizationPeople(includes);

        if (includes.filter(
            (x: string) => x.indexOf(IdentityPersonService.INCLUDE_TEAMS_AS_ATHLETE) !== -1,
        ).length === 0) {
            includes.push(IdentityPersonService.INCLUDE_TEAMS_AS_ATHLETE);
        }

        if (queryParams.filter(
            (x: string) => x.indexOf(IdentityPersonService.PARAM_USER_PERMISSIONS_META) !== -1,
        ).length === 0) {
            queryParams.push(`${IdentityPersonService.PARAM_USER_PERMISSIONS_META}=1`);
        }

        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(person.id, includes, queryParams);
        let role: string = roleId;

        if (roleId === Role.INVITED_COACH) {
            role = Role.COACH;
        }

        if (person.id) {
            // Update existing Team
            response = this.http.patch(
                apiRequestUrl,
                person.serialize(role, this.identitySportName, forceSaveAllValues),
            );
        } else {
            // Add a new Team
            response = this.http.post(
                apiRequestUrl,
                person.serialize(role, this.identitySportName, forceSaveAllValues),
            );
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityPerson()).deserialize(
                    response,
                    response.included,
                    this.identitySportName,
                )),
                tap((savedPerson: IdentityPerson) => this.saveTeamToPerson(person, savedPerson)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Merge a person (duplicate) into another (master)
     */
    public merge(masterPerson: IdentityPerson, duplicatePersonId: string): Observable<IdentityPerson> {
        // Clear cached results
        this.clearCache();
        const queryParams: string[] = this.appendGetFields();
        queryParams.push(`masterPersonId=${masterPerson.id}`);
        queryParams.push(`duplicatePersonId=${duplicatePersonId}`);
        const includes: string[] = this.appendOrganizationPeople([]);
        const apiRequestUrl: string = this.getApiUrl('merge', null, queryParams);

        const response: Observable<IJsonApiResponse> =
            this.http.patch(apiRequestUrl, masterPerson.serialize(null, this.identitySportName, true));

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityPerson()).deserialize(
                    response,
                    response.included,
                    this.identitySportName,
                )),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Email the auto merge report
     */
    public emailAutoMergeReport(): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl('email-auto-merge-report');

        return this.http.get(apiRequestUrl).pipe(map(() => true));
    }

    private saveTeamToPerson(toSavePerson: IdentityPerson, savedPerson: IdentityPerson): void {
        // Save the person's team
        savedPerson.teams = savedPerson.teams || [];
        toSavePerson.teams = toSavePerson.teams || [];

        if (toSavePerson.teams.length > 0) {
            toSavePerson.teams.forEach((team: IdentityTeam) => {
                if (team.id &&
                    savedPerson.teams.filter((x: IdentityTeam) => x.id === team.id).length === 0) {
                    // Save the person to this teams since they are not currently on it
                    this.identityTeamService.addAthletes(team.id, [savedPerson.id], true).subscribe(
                        (savedTeam: IdentityTeam) => {
                            savedPerson.teams.push(savedTeam);
                        },
                        (error: HttpErrorResponse) => {
                            console.log(`Error Saving Person to Team: ${error}`);
                        },
                    );
                }
            });
        }
    }

    /**
     * Delete the specified person.
     */
    public delete(identityPersonId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(identityPersonId);

        return this.http.delete(apiRequestUrl)
            .pipe(
                map(() => true),
                finalize(() => this.clearCache()),
            );
    }

    private appendGetFields(queryParams: string[] = []): string[] {
        queryParams = queryParams || [];

        // Only fetch specific fields
        if (queryParams.filter((x: string) => x.indexOf('fields[person]') !== -1).length === 0) {
            queryParams.push(`fields[person]=${IdentityPerson.getFields().join(',')}`);
        }

        return queryParams;
    }

    private appendOrganizationPeople(includes: string[] = []): string[] {
        includes = includes || [];
        if (!includes.find((x: string) => x.indexOf(IdentityPersonService.INCLUDE_ORGANIZATION_PEOPLE) !== -1)) {
            includes.push(IdentityPersonService.INCLUDE_ORGANIZATION_PEOPLE);
        }

        return includes;
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(personId: string, includes: string[] = null, queryParams: string[] = null): string {
        let url: string = `${this.identityApiBaseUrl}/people/`;

        if (personId) {
            url += personId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}
